/* ============================================================
 * AYU BEAUTY Masterclass Landing Page
 * "Strahlende Haut, glänzende Haare & starke Nägel –
 *  ohne teure Cremes & endlose Produkte."
 * 
 * Dienstag, 17. März 2026 um 19:00 Uhr
 * Premium Conversion-Design
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { IMAGES } from "@/lib/constants";
import { trpc } from "@/lib/trpc";
import {
  User, Phone, Mail, ArrowRight, Shield, CheckCircle, Leaf, Loader2,
  Clock, Users, Sparkles, Star, Lock, Zap, Heart, Droplets, Sun, Flower2
} from "lucide-react";

/* ── Countdown Timer Hook ── */
function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetDate));

  function getTimeLeft(target: Date) {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getTimeLeft(targetDate)), 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

/* ── Phone formatting ── */
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+49")) {
    const d = cleaned.slice(3);
    if (d.length <= 3) return `+49 ${d}`;
    if (d.length <= 6) return `+49 ${d.slice(0, 3)} ${d.slice(3)}`;
    if (d.length <= 10) return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
  }
  if (cleaned.startsWith("0")) {
    const d = cleaned.slice(1);
    if (d.length <= 3) return `+49 ${d}`;
    if (d.length <= 6) return `+49 ${d.slice(0, 3)} ${d.slice(3)}`;
    if (d.length <= 10) return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
  }
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.length > 0) {
    const d = cleaned;
    if (d.length <= 3) return `+49 ${d}`;
    if (d.length <= 6) return `+49 ${d.slice(0, 3)} ${d.slice(3)}`;
    if (d.length <= 10) return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return `+49 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
  }
  return value;
}

function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return false;
  const digits = cleaned.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

/* ── Countdown Display Component ── */
function CountdownTimer({ targetDate }: { targetDate: Date }) {
  const { days, hours, minutes, seconds } = useCountdown(targetDate);
  const units = [
    { value: days, label: "Tage" },
    { value: hours, label: "Std" },
    { value: minutes, label: "Min" },
    { value: seconds, label: "Sek" },
  ];

  return (
    <div className="flex gap-3 sm:gap-4 justify-center">
      {units.map((unit, i) => (
        <div key={i} className="flex flex-col items-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
            <span className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "var(--font-display)" }}>
              {String(unit.value).padStart(2, "0")}
            </span>
          </div>
          <span className="text-xs sm:text-sm text-white/70 mt-2" style={{ fontFamily: "var(--font-body)" }}>
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Main Component ── */
export default function MasterclassBeauty() {
  // Workshop: Dienstag, 17. März 2026, 19:00 Uhr CET
  const workshopDate = new Date("2026-03-17T19:00:00+01:00");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const [utmParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get("utm_source") || "masterclass",
      utmCampaign: params.get("utm_campaign") || "beauty-masterclass",
      utmMedium: params.get("utm_medium") || undefined,
      utmContent: params.get("utm_content") || undefined,
    };
  });

  const registerMutation = trpc.leads.register.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Lead", {
          content_name: "AYU Beauty Masterclass",
          content_category: "Masterclass",
        });
      }
    },
    onError: (err: { message?: string }) => {
      setSubmitting(false);
      setErrors({ email: err.message || "Ein Fehler ist aufgetreten." });
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("track", "ViewContent", {
        content_name: "Beauty Masterclass Landing",
        content_category: "Masterclass",
      });
    }
  }, []);

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = "Bitte gib deinen Vornamen ein";
    if (!lastName.trim()) e.lastName = "Bitte gib deinen Nachnamen ein";
    if (!phone.trim()) e.phone = "Bitte gib deine Telefonnummer ein";
    else if (!isValidPhone(phone.trim())) e.phone = "Bitte gib eine gültige Telefonnummer ein";
    if (!email.trim()) e.email = "Bitte gib deine E-Mail-Adresse ein";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = "Bitte gib eine gültige E-Mail ein";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    registerMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      ...utmParams,
    });
  }

  /* ── Beauty Color Palette ── */
  // Primary: Rose Gold oklch(0.78 0.06 30)
  // Accent: Soft Mauve oklch(0.65 0.08 330)
  // Background: Deep Plum oklch(0.14 0.04 320)

  return (
    <div className="min-h-screen bg-[oklch(0.12_0.04_320)]" style={{ fontFamily: "var(--font-body)" }}>

      {/* ═══════════════ HERO SECTION ═══════════════ */}
      <section className="relative overflow-hidden">
        {/* Background gradient — warm rose/plum tones */}
        <div className="absolute inset-0 bg-gradient-to-br from-[oklch(0.16_0.06_350)] via-[oklch(0.13_0.05_320)] to-[oklch(0.10_0.03_300)]" />
        <div className="absolute inset-0 opacity-8">
          <img src={IMAGES.mandala} alt="" className="w-full h-full object-cover" />
        </div>
        {/* Glow effects — rose gold */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[oklch(0.78_0.06_30/0.08)] blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[oklch(0.65_0.08_330/0.06)] blur-[120px]" />

        <div className="relative z-10 container py-8">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-12">
            <a href="/" className="flex items-center gap-3">
              <img src={IMAGES.logo} alt="AYU" className="h-10 w-auto" />
            </a>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Lock size={14} />
              <span>Plätze limitiert</span>
            </div>
          </nav>

          {/* Hero Grid */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center pb-16 lg:pb-24">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-[oklch(0.78_0.06_30/0.15)] border border-[oklch(0.78_0.06_30/0.3)]">
                <div className="w-2 h-2 rounded-full bg-[oklch(0.78_0.06_30)] animate-pulse" />
                <span className="text-sm font-medium text-[oklch(0.85_0.05_30)]">
                  Live Online Workshop — Dienstag, 17. März, 19:00 Uhr
                </span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Strahlende Haut,{" "}
                <span className="italic text-[oklch(0.78_0.06_30)]">glänzende Haare</span>{" "}
                & starke Nägel
              </h1>

              <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8 max-w-xl">
                Ohne teure Cremes & endlose Produkte. Entdecke, wie <strong className="text-white">Ayurveda von innen heraus</strong> wirkt
                — für natürliche Schönheit, die bleibt.
              </p>

              <div className="mb-10">
                <CountdownTimer targetDate={workshopDate} />
              </div>

              <button
                onClick={scrollToForm}
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[oklch(0.78_0.06_30)] to-[oklch(0.72_0.08_15)] text-[oklch(0.15_0.02_320)] font-bold text-lg transition-all hover:shadow-[0_0_40px_oklch(0.78_0.06_30/0.4)] hover:scale-105"
              >
                Jetzt kostenlos Platz sichern
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Right: Annam Image */}
            <div className="relative flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[oklch(0.78_0.06_30/0.2)] to-[oklch(0.65_0.08_330/0.2)] blur-xl" />
                <img
                  src={IMAGES.annamOrange}
                  alt="Annam Hanfstein"
                  className="relative rounded-3xl w-full max-w-md object-cover shadow-2xl"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-[oklch(0.78_0.06_30)]" />
                    <span className="text-white font-semibold">Beauty von innen</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ SOCIAL PROOF BAR ═══════════════ */}
      <section className="bg-gradient-to-r from-[oklch(0.78_0.06_30)] to-[oklch(0.72_0.08_15)] py-4">
        <div className="container flex flex-wrap items-center justify-center gap-6 sm:gap-12 text-[oklch(0.15_0.02_320)]">
          {[
            { icon: Star, text: "5.000 Jahre Ayurveda-Wissen" },
            { icon: Users, text: "180.000+ Menschen begleitet" },
            { icon: Flower2, text: "Natürliche Schönheit" },
            { icon: Shield, text: "100% kostenlos" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-semibold">
              <item.icon size={16} />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════ PROBLEM / AGITATION ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.14_0.04_320)]">
        <div className="container max-w-4xl text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Trockene Haut, brüchige Nägel,{" "}
            <span className="italic text-[oklch(0.78_0.06_30)]">stumpfes Haar?</span>
          </h2>
          <p className="text-lg text-white/60 leading-relaxed mb-12 max-w-2xl mx-auto">
            Du investierst in teure Produkte, Seren und Treatments — aber die Ergebnisse halten nie lange.
            Das liegt daran, dass echte Schönheit nicht von außen kommt. Sie beginnt <strong className="text-white">in deinem Körper</strong>.
          </p>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Droplets, title: "Deine Haut spiegelt", desc: "Was in deinem Inneren passiert — Verdauung, Hormone, Stress. Cremes behandeln nur die Oberfläche." },
              { icon: Sun, title: "Dein Glow fehlt", desc: "Nicht weil du zu wenig pflegst — sondern weil dein Körper andere Nährstoffe braucht als du denkst." },
              { icon: Flower2, title: "Natürliche Schönheit", desc: "Ayurveda kennt seit Jahrtausenden den Schlüssel: Ernährung, Routinen und Balance — typgerecht." },
            ].map((card, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[oklch(0.78_0.06_30/0.3)] transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[oklch(0.78_0.06_30/0.1)] flex items-center justify-center mb-4 mx-auto">
                  <card.icon size={24} className="text-[oklch(0.78_0.06_30)]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "var(--font-display)" }}>
                  {card.title}
                </h3>
                <p className="text-sm text-white/50">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ WHAT YOU'LL LEARN ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.12_0.04_320)]">
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-[oklch(0.78_0.06_30)] tracking-wider uppercase mb-4">
              Was dich erwartet
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              In 90 Minuten zu deinem{" "}
              <span className="italic text-[oklch(0.65_0.08_330)]">natürlichen Glow</span>
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                num: "01",
                title: "Dein Hauttyp nach Ayurveda — endlich richtig verstehen",
                desc: "Warum deine Haut sich so verhält, wie sie es tut — und was dein Dosha-Typ damit zu tun hat. Du lernst, welche Pflege und Ernährung wirklich zu dir passt.",
                icon: Droplets,
              },
              {
                num: "02",
                title: "Die 5 ayurvedischen Beauty-Geheimnisse",
                desc: "Einfache, natürliche Rituale für strahlende Haut, kräftige Haare und starke Nägel — ohne teure Produkte. Direkt umsetzbar ab morgen.",
                icon: Sparkles,
              },
              {
                num: "03",
                title: "Ernährung als Beauty-Booster",
                desc: "Welche Lebensmittel deinen Glow von innen aktivieren — und welche ihn sabotieren. Typgerechte Empfehlungen, die sofort wirken.",
                icon: Leaf,
              },
              {
                num: "04",
                title: "Morgen- und Abendroutine nach Ayurveda",
                desc: "Zwei einfache Routinen, die deine Haut, Haare und Nägel transformieren — in nur 10 Minuten am Tag.",
                icon: Sun,
              },
              {
                num: "05",
                title: "Live-Analyse: Dein persönlicher Beauty-Typ",
                desc: "Erlebe live, wie Annam einen echten Fall analysiert und individuelle Empfehlungen ableitet — vielleicht erkennst du dich wieder.",
                icon: Heart,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="group flex gap-6 p-6 sm:p-8 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-[oklch(0.78_0.06_30/0.2)] transition-all"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[oklch(0.78_0.06_30/0.15)] to-[oklch(0.65_0.08_330/0.1)] flex items-center justify-center">
                    <item.icon size={24} className="text-[oklch(0.78_0.06_30)]" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold text-[oklch(0.78_0.06_30)] tracking-wider">{item.num}</span>
                    <h3 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: "var(--font-display)" }}>
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-white/50 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={scrollToForm}
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[oklch(0.78_0.06_30)] to-[oklch(0.72_0.08_15)] text-[oklch(0.15_0.02_320)] font-bold text-lg transition-all hover:shadow-[0_0_40px_oklch(0.78_0.06_30/0.4)] hover:scale-105"
            >
              Kostenlos teilnehmen
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════ VORHER / NACHHER ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.14_0.04_320)]">
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-white leading-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Konventionelle Pflege vs.{" "}
              <span className="italic text-[oklch(0.78_0.06_30)]">Ayurvedische Beauty</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Konventionell */}
            <div className="p-8 rounded-3xl bg-white/[0.03] border border-red-500/20">
              <h3 className="text-xl font-bold text-red-400 mb-6" style={{ fontFamily: "var(--font-display)" }}>
                Konventionelle Pflege
              </h3>
              <div className="space-y-4">
                {[
                  "Teure Produkte mit kurzfristiger Wirkung",
                  "Einheits-Routinen für alle Hauttypen",
                  "Chemische Inhaltsstoffe belasten die Haut",
                  "Symptome werden behandelt, nicht Ursachen",
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-red-400 mt-0.5">✕</span>
                    <span className="text-white/50">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ayurvedisch */}
            <div className="p-8 rounded-3xl bg-[oklch(0.65_0.08_330/0.08)] border border-[oklch(0.65_0.08_330/0.3)]">
              <h3 className="text-xl font-bold text-[oklch(0.78_0.06_30)] mb-6" style={{ fontFamily: "var(--font-display)" }}>
                Ayurvedische Beauty
              </h3>
              <div className="space-y-4">
                {[
                  "Natürliche Schönheit von innen heraus",
                  "Typgerechte Pflege für deine Konstitution",
                  "Ernährung als mächtigstes Beauty-Tool",
                  "Langfristige Ergebnisse durch Balance",
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[oklch(0.78_0.06_30)] mt-0.5 flex-shrink-0" />
                    <span className="text-white/70">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ ANNAM SECTION ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.12_0.04_320)]">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[oklch(0.78_0.06_30/0.1)] to-transparent blur-xl" />
              <img
                src={IMAGES.annamBlue}
                alt="Annam Hanfstein"
                className="relative rounded-3xl w-full object-cover shadow-2xl"
              />
            </div>
            <div>
              <span className="inline-block text-sm font-semibold text-[oklch(0.78_0.06_30)] tracking-wider uppercase mb-4">
                Deine Gastgeberin
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Ich bin <span className="italic text-[oklch(0.78_0.06_30)]">Annam</span>.
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                Schönheit war für mich nie nur Oberfläche. In meiner Familie lernte ich früh:
                Wahre Ausstrahlung kommt von innen — aus Balance, Ernährung und dem Verständnis
                für den eigenen Körper.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                Über <strong className="text-white">180.000 Menschen</strong> durfte ich auf ihrem Weg begleiten —
                zu mehr Energie, innerer Ruhe und einer natürlichen Schönheit, die von innen strahlt.
              </p>
              <blockquote className="border-l-4 border-[oklch(0.78_0.06_30)] pl-6 italic text-white/70">
                „Deine Haut erzählt deine Geschichte. Lass mich dir zeigen, wie du sie zum Strahlen bringst
                — ganz natürlich, ganz du."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ TESTIMONIALS ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.14_0.04_320)]">
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-[oklch(0.78_0.06_30)] tracking-wider uppercase mb-4">
              Kundenstimmen
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Was andere über Annam sagen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Sinthi, aus Essen",
                text: "Seit ich Annams Tipps befolge, hat sich mein Hautbild komplett verändert. Keine teuren Cremes mehr — nur die richtigen Lebensmittel und einfache Routinen!",
              },
              {
                name: "Dani, aus Hamburg",
                text: "Ich hätte nie gedacht, dass Ernährung so viel mit meiner Haut zu tun hat. Annams Ansatz hat mir endlich geholfen, meinen natürlichen Glow zurückzubekommen.",
              },
              {
                name: "Susan, aus Viernheim",
                text: "Meine Haare waren jahrelang dünn und brüchig. Nach Annams ayurvedischen Empfehlungen sehen sie besser aus als je zuvor — und es war so einfach!",
              },
            ].map((t, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/[0.04] border border-white/[0.08]">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={16} className="text-[oklch(0.78_0.06_30)] fill-[oklch(0.78_0.06_30)]" />
                  ))}
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4 italic">„{t.text}"</p>
                <p className="text-white font-semibold text-sm">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ REGISTRATION FORM ═══════════════ */}
      <section className="py-20 sm:py-28 bg-[oklch(0.12_0.04_320)]" ref={formRef}>
        <div className="container max-w-3xl">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold text-[oklch(0.78_0.06_30)] tracking-wider uppercase mb-4">
              Kostenlos anmelden
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Sichere dir jetzt deinen <span className="italic text-[oklch(0.78_0.06_30)]">Platz</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto">
              Dienstag, 17. März um 19:00 Uhr — Live & kostenlos. Melde dich jetzt an und erhalte eine Erinnerung.
            </p>
          </div>

          <div className="mb-8">
            <CountdownTimer targetDate={workshopDate} />
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2 text-[oklch(0.78_0.06_30)] text-sm">
                <Lock size={14} />
                <span>Nur begrenzte Plätze verfügbar</span>
              </div>
            </div>
          </div>

          {submitted ? (
            <div className="p-10 rounded-3xl bg-white/[0.05] border border-[oklch(0.65_0.08_330/0.3)] text-center">
              <div className="w-20 h-20 rounded-full bg-[oklch(0.78_0.06_30/0.15)] flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-[oklch(0.78_0.06_30)]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "var(--font-display)" }}>
                Du bist dabei!
              </h3>
              <p className="text-white/60 mb-2">
                Wir haben dir eine Bestätigung an <strong className="text-white">{email}</strong> gesendet.
              </p>
              <p className="text-white/40 text-sm">
                Du erhältst rechtzeitig eine Erinnerung mit dem Zugangslink zum Workshop.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 sm:p-10 rounded-3xl bg-white/[0.05] border border-white/[0.1]">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {/* Vorname */}
                <div>
                  <label className="block text-sm text-white/60 mb-2">Vorname *</label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); setErrors((p) => ({ ...p, firstName: "" })); }}
                      placeholder="Dein Vorname"
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-[oklch(0.78_0.06_30/0.5)] transition-colors"
                    />
                  </div>
                  {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                </div>
                {/* Nachname */}
                <div>
                  <label className="block text-sm text-white/60 mb-2">Nachname *</label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => { setLastName(e.target.value); setErrors((p) => ({ ...p, lastName: "" })); }}
                      placeholder="Dein Nachname"
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-[oklch(0.78_0.06_30/0.5)] transition-colors"
                    />
                  </div>
                  {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Telefon */}
              <div className="mb-4">
                <label className="block text-sm text-white/60 mb-2">Telefon * (für Erinnerung)</label>
                <div className="relative">
                  <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(formatPhoneNumber(e.target.value)); setErrors((p) => ({ ...p, phone: "" })); }}
                    placeholder="+49 170 1234567"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-[oklch(0.78_0.06_30/0.5)] transition-colors"
                  />
                </div>
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-sm text-white/60 mb-2">E-Mail-Adresse *</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                    placeholder="deine@email.de"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-[oklch(0.78_0.06_30/0.5)] transition-colors"
                  />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-[oklch(0.78_0.06_30)] to-[oklch(0.72_0.08_15)] text-[oklch(0.15_0.02_320)] font-bold text-lg transition-all hover:shadow-[0_0_40px_oklch(0.78_0.06_30/0.4)] hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Wird angemeldet...
                  </>
                ) : (
                  <>
                    Jetzt kostenlos Platz sichern
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-4 mt-4 text-white/30 text-xs">
                <div className="flex items-center gap-1">
                  <Shield size={12} />
                  <span>DSGVO-konform</span>
                </div>
                <span>|</span>
                <span>Kein Spam, kein Abo</span>
                <span>|</span>
                <span>100% kostenlos</span>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="py-8 bg-[oklch(0.10_0.03_320)] border-t border-white/[0.05]">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4 text-white/30 text-sm">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-6 w-auto opacity-50" />
            <span>AYU-rveda.ai</span>
          </div>
          <div className="flex gap-6">
            <a href="/impressum" className="hover:text-white/60 transition-colors">Impressum</a>
            <a href="/datenschutz" className="hover:text-white/60 transition-colors">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
